import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
    const { language } = useLanguage();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        try {
            const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            
            const data = await response.json();
            
            if (response.ok) {
                setSuccess(true);
            } else {
                setError(data.detail || 'Failed to send reset email');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        }
        
        setLoading(false);
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 px-4 py-12">
                <div className="w-full max-w-md animate-fade-in">
                    <Card className="border-2">
                        <CardContent className="pt-8 pb-8 text-center">
                            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-emerald-600" />
                            </div>
                            <h2 className="text-xl font-semibold mb-2">
                                {language === 'zh' ? '郵件已發送' : 'Check Your Email'}
                            </h2>
                            <p className="text-muted-foreground mb-6">
                                {language === 'zh' 
                                    ? `我們已將密碼重置連結發送至 ${email}`
                                    : `We've sent a password reset link to ${email}`
                                }
                            </p>
                            <p className="text-sm text-muted-foreground mb-6">
                                {language === 'zh' 
                                    ? '連結將在1小時後過期'
                                    : 'The link will expire in 1 hour'
                                }
                            </p>
                            <Link to="/login">
                                <Button variant="outline" className="w-full">
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    {language === 'zh' ? '返回登入' : 'Back to Login'}
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 px-4 py-12">
            <div className="w-full max-w-md animate-fade-in">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-block">
                        <h1 className="font-serif text-2xl font-bold">
                            <span className="font-chinese text-wealth">通书</span> Plotter
                        </h1>
                    </Link>
                </div>
                
                <Card className="border-2">
                    <CardHeader className="space-y-1">
                        <CardTitle className="font-serif text-2xl">
                            {language === 'zh' ? '忘記密碼' : 'Forgot Password'}
                        </CardTitle>
                        <CardDescription>
                            {language === 'zh' 
                                ? '輸入您的電子郵件地址，我們將發送重置連結'
                                : 'Enter your email address and we\'ll send you a reset link'
                            }
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                                    {error}
                                </div>
                            )}
                            
                            <div className="space-y-2">
                                <Label htmlFor="email">
                                    {language === 'zh' ? '電子郵件' : 'Email'}
                                </Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="pl-10"
                                        data-testid="forgot-email-input"
                                    />
                                </div>
                            </div>
                            
                            <Button 
                                type="submit" 
                                className="w-full rounded-full" 
                                disabled={loading}
                                data-testid="forgot-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {language === 'zh' ? '發送中...' : 'Sending...'}
                                    </>
                                ) : (
                                    language === 'zh' ? '發送重置連結' : 'Send Reset Link'
                                )}
                            </Button>
                        </form>
                        
                        <div className="mt-6 text-center">
                            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center">
                                <ArrowLeft className="w-4 h-4 mr-1" />
                                {language === 'zh' ? '返回登入' : 'Back to Login'}
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ForgotPassword;
